/**
 * Иконки, которых нет в lucide-react.
 *
 * Нарисованы штрихом в сетке 24×24 и наследуют цвет через `currentColor` —
 * ровно как lucide, иначе в тёмном квадрате заголовка раздела и в боковом
 * меню они выглядели бы чужеродно.
 */

import type { SVGProps } from 'react';

/**
 * Раздел «Конструкции» — Марко Роса: базис с винтом, два кольца и передние опоры.
 * Геометрия по варианту v3 из задачи владельца от 26.09.2026, упрощённая для 22 px.
 */
export function ConstructionIcon({ size = 24, strokeWidth = 2, ...props }: StrokeIconProps) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
      {...props}
    >
      <g strokeWidth={strokeWidth * 0.8}>
        {/* Базис, разделённый по средней линии. */}
        <path d="M9 7h6a2 2 0 0 1 2 2v8a3 3 0 0 1-3 3h-4a3 3 0 0 1-3-3V9a2 2 0 0 1 2-2Z" />
        <path d="M12 7v5M12 15v5M7.5 7.7 5 4.5M16.5 7.7 19 4.5" />
        {/* Центральный винт. */}
        <rect x="10" y="12" width="4" height="3" rx="0.7" />
        {/* Кольца на молярах с короткими соединениями к базису. */}
        <path d="M5.7 15.5 7 17M18.3 15.5 17 17" />
        <path d="M3.4 11c1.7.2 2.8 1.3 2.6 2.9l-.2 1.3c-.3 1.4-1.5 2-3 1.5C1.4 16.3.9 15.5 1.2 14l.3-1.2c.3-1.3.8-2 1.9-1.8Z" />
        <path d="M20.6 11c-1.7.2-2.8 1.3-2.6 2.9l.2 1.3c.3 1.4 1.5 2 3 1.5 1.4-.4 1.9-1.2 1.6-2.7l-.3-1.2c-.3-1.3-.8-2-1.9-1.8Z" />
      </g>
    </svg>
  );
}

type StrokeIconProps = Omit<SVGProps<SVGSVGElement>, 'size'> & { size?: number | string; strokeWidth?: number };

/** Зуб: две доли коронки и раздвоенный корень, во всю сетку 24×24. */
const TOOTH = 'M12 5.5c-1.6-2-5.6-1.6-6.4 1.4-.7 2.2.2 4.2.7 5.9.5 1.5.7 3.1.8 4.6.1 1.9 1.9 1.9 2.2 0l.7-4h4l.7 4c.3 1.9 2.1 1.9 2.2 0 .1-1.5.3-3.1.8-4.6.5-1.7 1.4-3.7.7-5.9-.8-3-4.8-3.4-6.4-1.4Z';

/**
 * Раздел «Клиники» — клиника с филиалом.
 *
 * Два корпуса разной высоты с крестом на большом: у клиник лаборатории бывают
 * филиалы, и значок говорит именно об этом. Выбран владельцем из пяти
 * вариантов (15.09.2026) на смену домику с зубом, который на 22 px сливался
 * в пятно.
 */
export function ClinicIcon({ size = 24, strokeWidth = 2, ...props }: StrokeIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M10 21V9l5.5-4.5L21 9v12" />
      <path d="M3 21v-7l3.5-3 3.5 3" />
      <path d="M2 21h20" />
      <path d="M15.5 11.5v4M13.5 13.5h4" />
    </svg>
  );
}

/**
 * Раздел «Врачи» — человек со значком зуба.
 *
 * Стетоскоп говорил «врач вообще»; клиенты лаборатории — стоматологи, и зуб
 * у плеча называет их точнее. Выбран владельцем из пяти вариантов (15.09.2026).
 *
 * Зуб нарисован уменьшенным, поэтому толщина его линии делится на масштаб —
 * иначе при уменьшении она бы истончилась и выбилась из остальных значков.
 */
export function DoctorIcon({ size = 24, strokeWidth = 2, ...props }: StrokeIconProps) {
  const масштаб = 0.42;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="9.5" cy="7.5" r="3.8" />
      <path d="M2.5 21.5v-.5a6 6 0 0 1 6-6h2.5" />
      <g transform={`translate(17.2 16.6) scale(${масштаб}) translate(-12 -11.4)`} strokeWidth={strokeWidth / масштаб}>
        <path d={TOOTH} />
      </g>
    </svg>
  );
}

/**
 * Знак OrthoTechCRM — зубная дуга в круге.
 *
 * Прежде повторял значок «Конструкций»; с 15.09.2026 у раздела свой значок
 * (аппарат Хааса), а знак программы остался своим рисунком.
 *
 * Круг белый, дуга — цветом БОКОВОГО МЕНЮ (`--otc-accent-dark`), поэтому знак
 * перекрашивается вместе с темой в настройках: на бирюзовой теме дуга
 * бирюзовая, на синей — синяя. Инверсия относительно исходного рисунка
 * (чёрный круг, белая дуга) сделана намеренно: белый круг читается на тёмной
 * подложке меню, а чёрный сливался бы с ней.
 */
/**
 * Логотип лаборатории.
 *
 * Рисунок подключён МАСКОЙ, а не картинкой: цвет ему задаёт фон элемента,
 * то есть переменная темы. Логотип меняется вместе с выбранной палитрой и
 * не требует отдельного файла на каждую тему — а их пять.
 *
 * Исходник растровый и достаточно подробный (тонкие проволочные элементы),
 * поэтому оставлен растром: обводка такого контура вручную дала бы либо
 * потерю деталей, либо файл тяжелее самой картинки.
 */
export function OrthoTechLogo({
  size = 30,
  color = '#fff',
}: {
  size?: number | string;
  /** Цвет рисунка. По умолчанию белый — логотип стоит на тёмной шапке. */
  color?: string;
}) {
  return (
    <span
      role="img"
      aria-label="OrthoTechLab"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        background: color,
        WebkitMask: 'url(/logo.png?v=4) center/contain no-repeat',
        mask: 'url(/logo.png?v=4) center/contain no-repeat',
      }}
    />
  );
}
